function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * FlowsEntryRow is specific to the flows report where forward and reverse steps
 * are represented in the query builder.
 */

/* eslint-disable @typescript-eslint/no-use-before-define */

import React, { useState } from 'react';
import clamp from 'lodash/clamp';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { Placement } from 'common/components/tooltip/types';
import { Action, EntryPart, FiltersOperator, FlowsQueryEventEntry, isEventBasedEntry } from 'common/query-builder/query-entry-section/types';
// eslint-disable-next-line import/no-cycle
import { RowFilters } from 'common/query-builder/query-entry-section/components/filters';
import { RowSelector } from 'common/query-builder/query-entry-section/components/selector';
import { QueryReason } from 'common/report/util/query-tracking/types';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { useTranslation } from 'common/i18n/hooks';
import { BaseEntryRow } from '../base';
import { EntryRowProps } from '../types';
import { getEntryFilters } from '../row-util';
import { NumberOfSteps } from './number-of-steps';
import { hasEventValue } from './utils';
// eslint-disable-next-line import/no-cycle
import { helpers } from './helpers';
export function FlowsEntryRow(props: EntryRowProps<FlowsQueryEventEntry>) {
  const {
    t
  } = useTranslation();
  const [entryEl, setEntryEl] = useState<HTMLDivElement>(null);
  const handleFilterSectionUpdate = (filters: PropertyFilter[], filtersOperator: FiltersOperator, queryReason: QueryReason) => {
    props.updateEntry(Action.Edit, _objectSpread(_objectSpread({}, props.entry), {}, {
      filters,
      filtersOperator
    }), {
      queryReason
    });
  };
  const handleReverseStepsUpdate = (reverse: number) => {
    reverse = clamp(reverse, 0, 99);
    const delta = Math.abs(props.entry.reverse - reverse);
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      reverse
    });
    props.updateEntry(Action.EditFlowSteps, entry, {
      trackingProps: {
        reverse,
        delta
      }
    });
    return reverse;
  };
  const handleForwardStepsUpdate = (forward: number) => {
    forward = clamp(forward, 0, 99);
    const delta = Math.abs(props.entry.forward - forward);
    const entry = _objectSpread(_objectSpread({}, props.entry), {}, {
      forward
    });
    props.updateEntry(Action.EditFlowSteps, entry, {
      trackingProps: {
        forward,
        delta
      }
    });
    return forward;
  };
  const selectorIcon = props.entry.funnelStep ? "funnel-steps" : null;
  const selectorRenderer = () => <>
      {/** main selector */}
      <RowSelector {...props} contextPanelParentElement={entryEl} isPlaceholder={!hasEventValue(props.entry)} selectorIcon={selectorIcon} data-sentry-element="RowSelector" data-sentry-source-file="index.tsx" />
    </>;
  const suffixRenderer = () => {
    var _props$entry$forward, _props$entry$reverse, _props$entry$event;
    return <>
      {/* step controls */}
      <div className='mp-query-entry-row for-flows-steps'>
        <NumberOfSteps forward={(_props$entry$forward = props.entry.forward) !== null && _props$entry$forward !== void 0 ? _props$entry$forward : 0} reverse={(_props$entry$reverse = props.entry.reverse) !== null && _props$entry$reverse !== void 0 ? _props$entry$reverse : 0} onChangeForward={handleForwardStepsUpdate} onChangeReverse={handleReverseStepsUpdate} data-sentry-element="NumberOfSteps" data-sentry-source-file="index.tsx" />
      </div>

      {/* inline filters */}
      <RowFilters entry={props.entry} eventNames={(_props$entry$event = props.entry.event) !== null && _props$entry$event !== void 0 && _props$entry$event.value ? [props.entry.event.value] : []} filters={getEntryFilters(props.entry, props.sectionProps.behaviorMode)[EntryPart.Filters]} index={props.index} onUpdate={handleFilterSectionUpdate} hideEvents={!isEventBasedEntry(props.entry)} hasNewUI={props.hasNewUI} sectionProps={props.sectionProps} nestedSectionParams={props.nestedSectionParams} placeholders={props.placeholder} onAbortStage={() => props.setActivePart(EntryPart.Filters, "close")} data-sentry-element="RowFilters" data-sentry-source-file="index.tsx" />
    </>;
  };

  // renders a filter button shortcut
  const actionsRenderer = props.getFilteringCapabilities().canApplyFilter && (() => <ButtonReact theme={ButtonTheme.Tertiary} icon='filter-xs' tooltip={{
    'delay-in': 10,
    'delay-out': 10,
    placement: Placement.Top,
    text: t("query-builder.actions.filter")
  }} onClick={() => props.showPlaceholder(EntryPart.Filters)} />);
  return <BaseEntryRow ref={setEntryEl} {...props} showDefaultMetricUnitSelector={false} renderActions={actionsRenderer} renderSelector={selectorRenderer} renderSuffix={suffixRenderer} isPlaceholder={helpers.isPlaceholder(props)} overflowControls={props.getCommonOverflowControls()} data-sentry-element="BaseEntryRow" data-sentry-component="FlowsEntryRow" data-sentry-source-file="index.tsx" />;
}